import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { User } from './user';
import { AbilityInitializer } from './ability.service';
import { Observable, ReplaySubject } from 'rxjs';
import { authConfig } from './auth-config';
import { Router } from '@angular/router';
import { redirectUrlHandler } from './redirectUriHandler';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSubject$ = new ReplaySubject<User>(1);
  private user: User;

  constructor(private oauthService: OAuthService, private abilityInitializer: AbilityInitializer, private router: Router) {}

  getUser$(): Observable<User> {
    return this.userSubject$.asObservable();
  }

  logout() {
    this.abilityInitializer.removeAllAbilities();
    this.oauthService.logOut();
  }

  private initializeUser(claims: any) {
    if (claims) {
      this.user = {
        preferredUsername: claims.preferredUsername,
        authorities: claims.authorities,
        email: claims.email,
        familyName: claims.familyName,
        first_name: claims.first_name,
        full_name: claims.full_name,
        givenName: claims.givenName,
        last_name: claims.last_name,
        locale: claims.locale,
        name: claims.name,
        org_name: claims.org_name,
        org_id: claims.org_id,
        org_uuid: claims.org_uuid,
        user_name: claims.user_name,
        uuid: claims.uuid,
        language: claims.language
      };

      this.abilityInitializer.initializeAbilities(this.user.authorities);
      this.userSubject$.next(this.user);
    }
  }

  setupOauthService(dynamicEnv): Promise<boolean> {
    const loadedAuthConfig = dynamicEnv.authConfig;
    const authServerBaseUri = loadedAuthConfig.baseUri;

    // Overwrite our default authConfig with the values from the loadEnvironment.js/spa-wrapper-properties
    authConfig.loginUrl = authServerBaseUri + loadedAuthConfig.userUri;
    authConfig.clientId = loadedAuthConfig.clientId;
    authConfig.redirectUri = loadedAuthConfig.redirectUri;
    authConfig.issuer = authServerBaseUri;
    authConfig.tokenEndpoint = authServerBaseUri + loadedAuthConfig.accessTokenUri;
    authConfig.userinfoEndpoint = authServerBaseUri + loadedAuthConfig.infoUri;
    authConfig.requireHttps = loadedAuthConfig.requireHttps;
    authConfig.showDebugInformation = loadedAuthConfig.showDebugInformation;
    authConfig.useSilentRefresh = loadedAuthConfig.useSilentRefresh;
    authConfig.oidc = loadedAuthConfig.oidc;
    authConfig.scope = loadedAuthConfig.scope;
    authConfig.responseType = loadedAuthConfig.responseType;
    authConfig.clearHashAfterLogin = loadedAuthConfig.clearHashAfterLogin;

    // The silent-refresh.html can be on a different location based on the environment on which scip is installed

    let applicationBaseUrl = dynamicEnv.baseUrl; // Base url is without "scip-ui"-path-segment

    // The urls are set manually by support/dev within the loadEnvironment/spa.properties
    // This means it can contain, or it can miss the last slash :(
    if (applicationBaseUrl.slice(-1) !== '/') {
      applicationBaseUrl += '/';
    }

    if (window.location.href.includes('/scip-ui/')) {
      authConfig.silentRefreshRedirectUri = applicationBaseUrl + 'scip-ui/silent-refresh.html';
    } else {
      authConfig.silentRefreshRedirectUri = applicationBaseUrl + 'silent-refresh.html';
    }

    this.oauthService.configure(authConfig);
    this.oauthService.setupAutomaticSilentRefresh();
    // Let the lib do the check if the user already has a token
    // and if not directly forward him to the authentication server
    return this.oauthService.loadDiscoveryDocumentAndLogin().then((isAuthenticated: boolean) => {
      if (isAuthenticated) {
        const claims = this.oauthService.getIdentityClaims();
        if (this.user === undefined) {
          this.initializeUser(claims);
        }

        const initialUrlPath = redirectUrlHandler.getRedirectUrl();
        if (initialUrlPath?.length) {
          this.router.navigateByUrl(initialUrlPath);
          redirectUrlHandler.clearInitialUrl(); // after login and navigation we can throw away the intial url
        }
      }
      return isAuthenticated;
    });
  }
}
